import { staticFile } from '@common/utils'
import ReadMore from '@components/common/read-more'
import ModuleAllLessons from '@components/student-dashboard/course-modules/lesson-list'
import ShowAllLessonsButton from '@components/student-dashboard/course-modules/module-show-all-button'
import ModuleStat from '@components/student-dashboard/course-modules/module-stat'
import ModuleTopics from '@components/student-dashboard/course-modules/module-topics'
import {
  COURSE_MODULES_IMAGE_PATH,
  MODULE_DIFFICULTY,
  // MODULE_STATE,
} from '@lib/constants/course-modules'
import { ModuleStatType, ModuleType } from '@lib/types/course-modules'
import classNames from 'classnames'
import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'

type Props = {
  module: ModuleType
  areLessonsNotCollapsible?: boolean
  isOnDashboard?: boolean
  isOnCoursePage?: boolean
}

const ModuleCard = ({
  module: {
    logo,
    moduleNumber,
    title,
    topicsCovered,
    outline,
    lessons,
    moduleStats,
    state,
    difficulty,
  },
  areLessonsNotCollapsible = false,
  isOnDashboard = false,
  isOnCoursePage,
}: Props) => {
  const [showLessons, setShowLessons] = useState(areLessonsNotCollapsible)

  title = title.indexOf('Custom Module created') === 0 ? 'Custom Module' : title

  const moduleStatObjects: ModuleStatType[] = isOnCoursePage
    ? null
    : [
        {
          stat: moduleStats?.lessons,
          icon: 'lesson',
          title: 'Lessons',
          tooltipMessage: `You have completed ${moduleStats?.lessons.completed} out of ${moduleStats?.lessons.total} lessons`,
        },
        {
          stat: moduleStats?.assignments,
          icon: 'projects',
          title: 'Projects',
          tooltipMessage: `You have completed ${moduleStats?.assignments.completed} out of ${moduleStats?.assignments.total} projects`,
        },
        {
          stat: moduleStats?.quizzes,
          icon: 'quizzes',
          title: 'Quizzes',
          tooltipMessage: `You have completed ${moduleStats?.quizzes.completed} out of ${moduleStats?.quizzes.total} quizzes`,
        },
      ]

  const handleCreateEvent = () => {
    if (isOnCoursePage) {
      if (showLessons) {
        setShowLessons(false)
      } else {
        setShowLessons(true)
      }
    }
  }

  return (
    <div
      onClick={handleCreateEvent}
      className={`${
        isOnCoursePage
          ? 'flex border border-grey-500 bg-grey-200 bg-opacity-75'
          : 'bg-white'
      } select-none cursor-pointer`}
    >
      {isOnCoursePage && (
        <div className="lg:hidden flex pl-0 self-center">
          {showLessons ? (
            <ChevronUp
              className="text-grey group-hover:text-orange"
              width={35}
            />
          ) : (
            <ChevronDown
              className="text-grey group-hover:text-orange"
              width={35}
            />
          )}
        </div>
      )}
      <div
        className={`${
          isOnCoursePage
            ? 'py-4 lg:pl-5 pr-3 lg:pr-0 lg:space-x-5 lg:flex'
            : 'p-10 gap-5 lg:gap-10 rounded-lg flex flex-col lg:flex-row'
        } items-start`}
      >
        <div
          className={`${
            !isOnCoursePage && 'lg:space-y-3'
          } flex flex-col lg:items-center`}
        >
          <div className={`${!isOnCoursePage && 'lg:pb-3'} w-20 md:p-0`}>
            <img
              src={
                logo
                  ? logo
                  : staticFile(
                      `${COURSE_MODULES_IMAGE_PATH}module-card/fallback-module-icon.png`
                    )
              }
              className={`${
                isOnCoursePage
                  ? 'hidden lg:block'
                  : 'rounded-full border-2 border-grey-400'
              }`}
              alt={title}
              width="80px"
              height="80px"
            />
          </div>
          <p
            className={`${
              isOnCoursePage ? 'text-orange' : 'lg:block hidden'
            }  font-600`}
          >
            Module {moduleNumber}
          </p>
        </div>
        <div className={`${!isOnCoursePage && 'w-full'} flex flex-col`}>
          <div className="flex flex-col text-grey space-y-3 leading-tight">
            <div className="flex lg:flex-row flex-col items-start lg:items-center space-y-1 lg:space-y-0 lg:space-x-2">
              <h3 className="text-black font-600 text-lg">{title}</h3>
              {difficulty && (
                <p
                  className={classNames(
                    'rounded-md px-2 text-sm lg:text-base',
                    {
                      'bg-green-50': difficulty === MODULE_DIFFICULTY.BEGINNER,
                      'bg-yellow-100':
                        difficulty === MODULE_DIFFICULTY.INTERMEDIATE,
                      'bg-red-100': difficulty === MODULE_DIFFICULTY.EXPERT,
                    }
                  )}
                >
                  {difficulty} Level
                </p>
              )}
            </div>
            {topicsCovered && topicsCovered.length !== 0 && (
              <ModuleTopics topicsCovered={topicsCovered} />
            )}
            <ReadMore
              lines={3}
              isOnCoursePage={isOnCoursePage}
              isDisplayWholetText={showLessons}
            >
              {outline}
            </ReadMore>
            {moduleStatObjects && (
              <div className="flex pt-3 justify-between max-w-4xl">
                {moduleStatObjects
                  .filter(({ stat }) => stat.total)
                  .map(
                    ({
                      icon,
                      stat,
                      title,
                      isGamificationPointsStat,
                      tooltipMessage,
                    }) => (
                      <ModuleStat
                        key={icon}
                        icon={icon}
                        stat={stat}
                        title={title}
                        moduleState={state}
                        isGamificationPointsStat={isGamificationPointsStat}
                        tooltipMessage={tooltipMessage}
                      />
                    )
                  )}
              </div>
            )}
            {!areLessonsNotCollapsible && !isOnCoursePage && (
              <ShowAllLessonsButton
                setShowLessons={setShowLessons}
                showLessons={showLessons}
              />
            )}
          </div>
          <ModuleAllLessons
            showLessons={showLessons}
            moduleState={state}
            lessons={lessons}
            isOnDashboard={isOnDashboard}
            isOnCoursePage={isOnCoursePage}
          />
        </div>

        {isOnCoursePage && (
          <div className="hidden lg:flex pt-10 pr-5 order">
            {showLessons ? (
              <ChevronUp
                className="text-grey group-hover:text-orange"
                width={35}
              />
            ) : (
              <ChevronDown
                className="text-grey group-hover:text-orange"
                width={35}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ModuleCard
